import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import Navbar from '../../components/navbar/navbar'
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  FiletypePdf,
  TrashFill,
  XCircleFill,
} from 'react-bootstrap-icons'
import { CustomButton } from '../../components/CustomButton'
import { CustomSpinner } from '../../components/CustomSpinner'
import ReactPaginate from 'react-paginate'
import { useOrderTable } from '../../hooks/useOrderTable'
import { toast } from 'react-toastify'

import HistoricoTransferenciaService from '../../services/database/HistoricoTransferenciaService'
import EmpresasDatabaseService from '../../services/database/EmpresasDatabaseService'

import { formatPrice } from '../../utils/formatPrice'
import { formatDate } from '../../utils/formatDate'

import noFoundItemsImg from '../../assets/illustration/no-found-items.svg'
import {
  startOfDay,
  isWithinInterval,
  parseISO,
  isValid,
  isAfter,
  isBefore,
} from 'date-fns'

import generatePDF, { Margin, Resolution } from 'react-to-pdf'

export function ConsultarTransferencias() {
  const [transferencias, setTransferencias] = useState([])
  const [databases, setDatabases] = useState([])

  const [isTransferenciasLoading, setIsTransferenciasLoading] = useState(true)
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(true)
  const [isLoadingPDFGenerate, setIsLoadingPDFGenerate] = useState(false)

  const [bancoOrigem, setBancoOrigem] = useState('')
  const [bancoDestino, setBancoDestino] = useState('')
  const [tipo, setTipo] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [itemOffset, setItemOffset] = useState(0)
  const [itemsPerPage] = useState(10)
  const endOffset = itemOffset + itemsPerPage

  const [pageCount, setPageCount] = useState(0)

  const targetRef = useRef(null)

  let databasesOptions = databases.map((database) => ({
    label: database.nome_conta,
    value: database.id_conta,
  }))

  const {
    sortedArray: sortedTransferencias,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(transferencias, 'id')

  function handlePageClick(event) {
    const newOffset = (event.selected * itemsPerPage) % transferencias.length

    setItemOffset(newOffset)
  }

  const filteredTransferencias = useMemo(() => {
    let transfersFiltered = [...sortedTransferencias]

    if (bancoOrigem) {
      transfersFiltered = transfersFiltered.filter(
        (transfer) => transfer.banco_origem === bancoOrigem.label,
      )
    }

    if (bancoDestino) {
      transfersFiltered = transfersFiltered.filter(
        (transfer) => transfer.banco_destino === bancoDestino.label,
      )
    }

    transfersFiltered = transfersFiltered.filter((transfer) =>
      String(transfer.tipo).toLowerCase().includes(String(tipo).toLowerCase()),
    )

    if (startDate && endDate) {
      const start = startOfDay(parseISO(startDate))
      const end = startOfDay(parseISO(endDate))

      if (isValid(start) && isValid(end)) {
        if (!isAfter(start, end) && !isBefore(end, start)) {
          transfersFiltered = transfersFiltered.filter((transfer) => {
            const transferDate = startOfDay(parseISO(transfer.data))

            if (isWithinInterval(transferDate, { start, end })) {
              return transfer
            }
          })
        }
      }
    }

    setPageCount(Math.ceil(transfersFiltered.length / itemsPerPage))

    return transfersFiltered.slice(itemOffset, endOffset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bancoDestino,
    bancoOrigem,
    endDate,
    endOffset,
    itemOffset,
    itemsPerPage,
    sortedTransferencias,
    startDate,
    tipo,
    orderType.field,
    orderType.type,
  ])

  async function handleClearFilter() {
    setBancoOrigem('')
    setBancoDestino('')
    setStartDate('')
    setEndDate('')
    setTipo('')
  }

  const totalCosts = useMemo(() => {
    const costs = filteredTransferencias.reduce((acc, item) => {
      acc = acc + item.precocusto

      return acc
    }, 0)

    return costs
  }, [filteredTransferencias])

  async function loadTransferencias() {
    setIsTransferenciasLoading(true)

    try {
      const data = await HistoricoTransferenciaService.getAll()

      setTransferencias(data)
    } catch (err) {
      console.log(`Erro ao carregar as transferências: ${err}`)
      toast.error('Erro ao carregar as transferências!')
    } finally {
      setIsTransferenciasLoading(false)
    }
  }

  async function loadDatabases() {
    setIsLoadingDatabases(true)

    try {
      const empresasData = await EmpresasDatabaseService.getAll()

      setDatabases(empresasData)
    } catch (err) {
      console.log(`Erro ao pegar as bancos de dados da empresa: ${err}`)
      toast.error('Erro ao pegar as bancos de dados da empresa!')
    } finally {
      setIsLoadingDatabases(false)
    }
  }

  async function gerarRelatorio() {
    setIsLoadingPDFGenerate(true)

    try {
      const options = {
        method: 'open',
        resolution: Resolution.EXTREME,
        page: {
          margin: Margin.SMALL,
          orientation: 'p',
        },
      }

      await generatePDF(targetRef, options)
    } catch (err) {
      console.log(`Erro ao gerar o PDF ${err}`)

      toast.error(`Erro ao gerar o PDF, tente novamente mais tarde!`)
    } finally {
      setIsLoadingPDFGenerate(false)
    }
  }

  useEffect(() => {
    loadTransferencias()
    loadDatabases()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page">
        <h1>Consultar Transferências</h1>

        <Row
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 10,
          }}
        >
          <Col xs={4}>
            <Form.Group>
              <Form.Label>Banco de Origem</Form.Label>

              <Select
                onChange={(option) => {
                  setBancoDestino('')
                  setBancoOrigem(option)
                }}
                placeholder={
                  isLoadingDatabases
                    ? 'Carregando bancos de dados...'
                    : 'Selecione um banco'
                }
                isDisabled={isLoadingDatabases}
                value={bancoOrigem}
                options={databasesOptions}
              />
            </Form.Group>
          </Col>

          <Col xs={4}>
            <Form.Group>
              <Form.Label>Banco de Destino</Form.Label>
              <Select
                placeholder={
                  isLoadingDatabases
                    ? 'Carregando bancos de dados...'
                    : 'Selecione um banco'
                }
                value={bancoDestino}
                onChange={(option) => {
                  setBancoDestino(option)
                }}
                isDisabled={isLoadingDatabases}
                options={databasesOptions.filter(
                  (database) => database.value !== bancoOrigem.value,
                )}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          }}
        >
          <Col>
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
              >
                <option value="">Ambos</option>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <Form.Label>De:</Form.Label>

            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
              }}
            />
          </Col>

          <Col>
            <Form.Label>Até:</Form.Label>

            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>

          <Col
            style={{
              display: 'flex',
            }}
          >
            <CustomButton
              title="Limpar Filtro"
              color="#e62222"
              onClick={handleClearFilter}
            >
              <XCircleFill />
            </CustomButton>
          </Col>
        </Row>

        {isTransferenciasLoading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '25px',
            }}
          >
            <CustomSpinner />
          </div>
        ) : transferencias.length === 0 ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'start',
                alignItems: 'start',
              }}
            >
              <h1
                style={{
                  fontSize: 24,
                }}
              >
                Sem transferências encontradas!
              </h1>

              <CustomButton
                title="Tentar Novamente"
                styles={{
                  marginLeft: 0,
                }}
                onClick={loadTransferencias}
              >
                <ArrowClockwise />
              </CustomButton>
            </div>

            <img
              src={noFoundItemsImg}
              style={{
                width: 400,
              }}
            />
          </div>
        ) : (
          <div>
            <Table ref={targetRef}>
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('id')}>
                    <span>ID</span>
                    <CaretUp color={check('id', 'asc')} />
                    <CaretDown color={check('id', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('produto_sku')}>
                    <span>SKU Produto</span>
                    <CaretUp color={check('produto_sku', 'asc')} />
                    <CaretDown color={check('produto_sku', 'desc')} />
                  </th>

                  <th>Descrição</th>

                  <th>Tipo</th>
                  <th>Banco Origem</th>
                  <th>Banco Destino</th>

                  <th onClick={() => handleOrganize('data')}>
                    <span>Data</span>
                    <CaretUp color={check('data', 'asc')} />
                    <CaretDown color={check('data', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('quantidade')}>
                    <span>Quantidade</span>
                    <CaretUp color={check('quantidade', 'asc')} />
                    <CaretDown color={check('quantidade', 'desc')} />
                  </th>

                  <th>Custo</th>
                  <th>Ações</th>
                </tr>
              </thead>

              <tbody>
                {filteredTransferencias.map((transferencia) => (
                  <Transferencia
                    key={transferencia.id}
                    transferencia={transferencia}
                    loadTransferencias={loadTransferencias}
                  />
                ))}
                <tr>
                  <td colSpan={9}>
                    Total: <strong>{formatPrice(totalCosts)}</strong>
                  </td>
                </tr>
              </tbody>
            </Table>

            <CustomButton
              title="Gerar Relatório"
              color="#9322e6"
              onClick={gerarRelatorio}
              isLoading={isLoadingPDFGenerate}
              isDisabled={filteredTransferencias.length === 0}
            >
              <FiletypePdf />
            </CustomButton>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </div>
        )}
      </div>
    </>
  )
}

function Transferencia({ transferencia, loadTransferencias }) {
  const [isTransferenciaDeleteLoading, setIsTransferenciaDeleteLoading] =
    useState(false)

  async function handleDeleteTransferencia(id) {
    setIsTransferenciaDeleteLoading(true)
    try {
      await HistoricoTransferenciaService.delete(id)

      toast.success('Transferência deletada com sucesso!')

      loadTransferencias()
    } catch (error) {
      console.log(`Erro ao deletar a transferência: ${error}`)

      return toast.error(
        'Erro ao deletar a transferência, tente novamente mais tarde!',
      )
    } finally {
      setIsTransferenciaDeleteLoading(false)
    }
  }

  return (
    <tr>
      <td>{transferencia.id}</td>
      <td>{transferencia.produto_sku}</td>
      <td>{transferencia.descricao}</td>
      <td>{transferencia.tipo}</td>
      <td>{transferencia.banco_origem}</td>
      <td>{transferencia.banco_destino}</td>
      <td>{formatDate(transferencia.data, 'dd/MM/yyyy, HH:mm:ss')}</td>
      <td>{transferencia.quantidade}</td>
      <td>{formatPrice(transferencia.precocusto)}</td>
      <td>
        <OverlayTrigger overlay={<Tooltip>Deletar</Tooltip>}>
          <CustomButton
            color="#e62222"
            onClick={() => handleDeleteTransferencia(transferencia.id)}
            isLoading={isTransferenciaDeleteLoading}
          >
            <TrashFill />
          </CustomButton>
        </OverlayTrigger>
      </td>
    </tr>
  )
}
